import React, { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import { Send, Loader2, Users, ChevronRight, Calendar, Trash2, Clock } from "lucide-react";
import {
  fetchNewsletterAudience,
  broadcastNewsletter,
  scheduleNewsletter,
  fetchScheduledNewsletters,
  cancelScheduledNewsletter,
} from "@/lib/adminApi";

const SEGMENT_OPTIONS = [
  { value: "", label: "Todos os inscritos" },
  { value: "gramatica", label: "Gramática" },
  { value: "cultura", label: "Cultura & viagem" },
  { value: "provas", label: "Provas (DELF/DALF/TEF)" },
];

const DEFAULT_TEMPLATE = `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;max-width:600px;margin:0 auto;padding:20px;color:#1e2a5a">
  <h1 style="font-size:24px;margin:0 0 16px">Olá, {name}! 🇫🇷</h1>
  <p style="font-size:16px;line-height:1.6;color:#3a3a3a">
    Aqui está a dica da semana:
  </p>
  <blockquote style="border-left:3px solid #d9381e;padding-left:16px;margin:24px 0;font-style:italic;color:#1e2a5a">
    « Seu conteúdo aqui »
  </blockquote>
  <p style="font-size:16px;line-height:1.6;color:#3a3a3a">
    Explicação em 2-3 frases...
  </p>
  <p style="margin-top:32px">
    <a href="https://npdlbr.com.br/blog" style="display:inline-block;background:#d9381e;color:#f4f1ea;padding:12px 24px;text-decoration:none;border-radius:24px;font-weight:600">
      Ler mais no blog
    </a>
  </p>
  <p style="margin-top:24px;color:#5c5c5c">
    À bientôt !<br/>
    Prof Lionel
  </p>
</div>`;

// Compute next Thursday 09:00 in the browser's local timezone, formatted for
// datetime-local input (YYYY-MM-DDTHH:mm). Used as the scheduler default.
function defaultThursday9amLocal() {
  const d = new Date();
  d.setHours(9, 0, 0, 0);
  const dow = d.getDay(); // 0=Sun..4=Thu
  const daysAhead = (4 - dow + 7) % 7 || 7; // always in the future
  d.setDate(d.getDate() + daysAhead);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatLocalDatetime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

const NewsletterBroadcast = () => {
  const [audience, setAudience] = useState(null);
  const [subject, setSubject] = useState("");
  const [htmlBody, setHtmlBody] = useState(DEFAULT_TEMPLATE);
  const [segment, setSegment] = useState("");
  const [preview, setPreview] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  // Scheduling state
  const [mode, setMode] = useState("now"); // "now" | "schedule"
  const [sendAt, setSendAt] = useState(() => defaultThursday9amLocal());
  const [scheduled, setScheduled] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);

  const refreshScheduled = async () => {
    setScheduledLoading(true);
    try {
      const list = await fetchScheduledNewsletters();
      setScheduled(list);
    } catch {
      /* ignore */
    } finally {
      setScheduledLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const a = await fetchNewsletterAudience();
        setAudience(a);
      } catch {
        setAudience({ total: 0, by_segment: { all: 0 } });
      }
    })();
    refreshScheduled();
  }, []);

  const segmentCount = audience
    ? segment
      ? audience.by_segment?.[segment] ?? 0
      : audience.by_segment?.all ?? audience.total ?? 0
    : null;

  const handleAskConfirm = (e) => {
    e.preventDefault();
    if (!subject.trim()) {
      toast.error("Coloca um assunto.");
      return;
    }
    if (!htmlBody.trim()) {
      toast.error("O corpo do e-mail está vazio.");
      return;
    }
    if (!segmentCount) {
      toast.error("Nenhum inscrito nesse segmento — nada a enviar.");
      return;
    }
    if (mode === "schedule") {
      if (!sendAt) {
        toast.error("Escolha uma data e hora.");
        return;
      }
      const target = new Date(sendAt);
      if (Number.isNaN(target.getTime()) || target <= new Date()) {
        toast.error("A data deve estar no futuro.");
        return;
      }
    }
    setConfirming(true);
  };

  const handleSend = async () => {
    setSending(true);
    setConfirming(false);
    try {
      if (mode === "schedule") {
        // Convert local datetime-local -> ISO UTC
        const iso = new Date(sendAt).toISOString();
        await scheduleNewsletter({
          subject: subject.trim(),
          html_body: htmlBody,
          segment: segment || null,
          send_at: iso,
        });
        toast.success(`Agendado para ${formatLocalDatetime(iso)}.`);
        await refreshScheduled();
      } else {
        const r = await broadcastNewsletter({
          subject: subject.trim(),
          html_body: htmlBody,
          segment: segment || null,
        });
        setLastResult(r);
        toast.success(
          `Enviado para ${r.sent} inscrito(s). ${r.failed} falha(s).`
        );
        // Refresh audience count (in case of unsubscribes triggered mid-flight)
        try {
          const a = await fetchNewsletterAudience();
          setAudience(a);
        } catch {
          /* ignore */
        }
      }
    } catch (err) {
      toast.error(mode === "schedule" ? "Não foi possível agendar." : "Não foi possível enviar. Tenta de novo?");
    } finally {
      setSending(false);
    }
  };

  const handleCancelScheduled = async (id) => {
    if (!window.confirm("Cancelar este agendamento?")) return;
    try {
      await cancelScheduledNewsletter(id);
      toast.success("Agendamento cancelado.");
      await refreshScheduled();
    } catch {
      toast.error("Não foi possível cancelar.");
    }
  };

  const previewHtml = htmlBody.replace(/\{name\}/g, "Mariana");

  return (
    <div className="mt-8 space-y-6" data-testid="newsletter-broadcast-tab">
      <div className="bg-white border border-[#e5e3db] rounded-xl p-5 sm:p-6">
        <div className="flex items-center gap-3 text-[#1e2a5a]">
          <Users className="h-5 w-5" strokeWidth={1.75} />
          <p className="text-sm">
            Audiência total:{" "}
            <span className="font-semibold" data-testid="broadcast-audience-total">
              {audience ? audience.total : "—"}
            </span>{" "}
            inscrito(s)
          </p>
        </div>
        {audience && audience.by_segment && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {SEGMENT_OPTIONS.filter((s) => s.value).map((s) => (
              <span
                key={s.value}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#f0efea] px-3 py-1 text-[#5c5c5c]"
              >
                {s.label}:{" "}
                <strong className="text-[#1e2a5a]">
                  {audience.by_segment[s.value] ?? 0}
                </strong>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#e5e3db] rounded-xl p-1.5 inline-flex">
        <button
          type="button"
          onClick={() => setMode("now")}
          data-testid="broadcast-mode-now"
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            mode === "now"
              ? "bg-[#1e2a5a] text-[#f4f1ea]"
              : "text-[#5c5c5c] hover:text-[#1e2a5a]"
          }`}
        >
          <Send className="h-3.5 w-3.5" strokeWidth={2} />
          Enviar agora
        </button>
        <button
          type="button"
          onClick={() => setMode("schedule")}
          data-testid="broadcast-mode-schedule"
          className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            mode === "schedule"
              ? "bg-[#1e2a5a] text-[#f4f1ea]"
              : "text-[#5c5c5c] hover:text-[#1e2a5a]"
          }`}
        >
          <Calendar className="h-3.5 w-3.5" strokeWidth={2} />
          Agendar
        </button>
      </div>

      <form
        onSubmit={handleAskConfirm}
        className="bg-white border border-[#e5e3db] rounded-xl p-5 sm:p-6 space-y-4"
      >
        <div>
          <label
            htmlFor="broadcast-subject"
            className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c]"
          >
            Assunto (subject)
          </label>
          <input
            id="broadcast-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="🥐 A dica da semana do Prof Lionel — passé composé"
            data-testid="broadcast-subject-input"
            className="mt-2 w-full rounded-lg border border-[#e5e3db] px-4 py-2.5 text-sm focus:border-[#d9381e] focus:outline-none focus:ring-2 focus:ring-[#d9381e]/15"
          />
        </div>

        <div>
          <label
            htmlFor="broadcast-segment"
            className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c]"
          >
            Segmento
          </label>
          <select
            id="broadcast-segment"
            value={segment}
            onChange={(e) => setSegment(e.target.value)}
            data-testid="broadcast-segment-select"
            className="mt-2 w-full rounded-lg border border-[#e5e3db] px-4 py-2.5 text-sm bg-white focus:border-[#d9381e] focus:outline-none focus:ring-2 focus:ring-[#d9381e]/15"
          >
            {SEGMENT_OPTIONS.map((s) => (
              <option key={s.value || "all"} value={s.value}>
                {s.label} ({audience?.by_segment?.[s.value || "all"] ?? 0})
              </option>
            ))}
          </select>
        </div>

        {mode === "schedule" && (
          <div>
            <label
              htmlFor="broadcast-send-at"
              className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c]"
            >
              Data e hora (fuso local)
            </label>
            <input
              id="broadcast-send-at"
              type="datetime-local"
              value={sendAt}
              onChange={(e) => setSendAt(e.target.value)}
              data-testid="broadcast-send-at-input"
              className="mt-2 w-full rounded-lg border border-[#e5e3db] px-4 py-2.5 text-sm bg-white focus:border-[#d9381e] focus:outline-none focus:ring-2 focus:ring-[#d9381e]/15"
            />
            <p className="mt-2 text-xs text-[#5c5c5c]">
              Sugestão: quinta-feira 09h (mais leituras).
            </p>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between">
            <label
              htmlFor="broadcast-body"
              className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c]"
            >
              Corpo (HTML) — use{" "}
              <code className="bg-[#f0efea] px-1.5 py-0.5 rounded">{"{name}"}</code>{" "}
              para personalizar
            </label>
            <button
              type="button"
              onClick={() => setPreview((p) => !p)}
              data-testid="broadcast-toggle-preview"
              className="text-xs link-underline"
            >
              {preview ? "Editar" : "Preview"}
            </button>
          </div>
          {preview ? (
            <div
              className="mt-2 min-h-[260px] rounded-lg border border-[#e5e3db] bg-[#f0efea] p-4 overflow-auto"
              data-testid="broadcast-preview"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }}
            />
          ) : (
            <textarea
              id="broadcast-body"
              value={htmlBody}
              onChange={(e) => setHtmlBody(e.target.value)}
              rows={12}
              data-testid="broadcast-body-textarea"
              className="mt-2 w-full rounded-lg border border-[#e5e3db] px-4 py-2.5 text-xs font-mono leading-relaxed focus:border-[#d9381e] focus:outline-none focus:ring-2 focus:ring-[#d9381e]/15"
            />
          )}
        </div>

        <div className="pt-2 flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={sending}
            data-testid="broadcast-submit"
            className="group inline-flex items-center gap-2 rounded-full bg-[#d9381e] px-6 py-2.5 text-sm font-semibold text-[#f4f1ea] transition-colors hover:bg-[#1e2a5a] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {mode === "schedule" ? (
              <Calendar className="h-4 w-4" strokeWidth={2} />
            ) : (
              <Send className="h-4 w-4" strokeWidth={2} />
            )}
            {mode === "schedule"
              ? `Agendar para ${segmentCount ?? "—"} inscrito(s)`
              : `Enviar para ${segmentCount ?? "—"} inscrito(s)`}
          </button>
          <p className="text-xs text-[#5c5c5c]">
            Um rodapé de cancelamento é adicionado automaticamente.
          </p>
        </div>
      </form>

      {lastResult && (
        <div
          data-testid="broadcast-last-result"
          className="rounded-xl border border-[#1e7a3e]/30 bg-[#e8f4ea] px-5 py-4 text-sm"
        >
          <p className="text-[#1e7a3e] font-semibold">Último envio</p>
          <p className="mt-1 text-[#3a3a3a]">
            {lastResult.sent} enviado(s) · {lastResult.failed} falha(s) · segmento:{" "}
            {lastResult.segment || "todos"}
          </p>
        </div>
      )}

      {/* Scheduled newsletters — upcoming + recent */}
      <div
        className="bg-white border border-[#e5e3db] rounded-xl p-5 sm:p-6"
        data-testid="scheduled-newsletters-list"
      >
        <div className="flex items-center gap-2 text-[#1e2a5a]">
          <Clock className="h-4 w-4" strokeWidth={1.75} />
          <p className="text-sm font-semibold">Agendamentos ({scheduled.length})</p>
        </div>
        {scheduledLoading ? (
          <p className="mt-4 text-sm text-[#5c5c5c]">Carregando…</p>
        ) : scheduled.length === 0 ? (
          <p className="mt-4 text-sm text-[#5c5c5c]">
            Nenhum agendamento por enquanto. Crie um usando o modo « Agendar » acima.
          </p>
        ) : (
          <ul className="mt-4 space-y-2 text-sm">
            {scheduled.map((s) => (
              <li
                key={s.id}
                data-testid={`scheduled-item-${s.id}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#e5e3db] px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[#1e2a5a] font-medium truncate">
                    {s.subject}
                  </p>
                  <p className="mt-0.5 text-xs text-[#5c5c5c]">
                    {formatLocalDatetime(s.send_at)} ·{" "}
                    {s.segment ? `Segmento: ${s.segment}` : "Todos"} ·{" "}
                    <span
                      className={
                        s.status === "pending"
                          ? "text-[#d9381e]"
                          : s.status === "sent"
                          ? "text-[#1e7a3e]"
                          : s.status === "failed"
                          ? "text-[#d9381e]"
                          : "text-[#5c5c5c]"
                      }
                    >
                      {s.status}
                    </span>
                    {s.result &&
                      ` · ${s.result.sent}/${s.result.total} enviados`}
                  </p>
                </div>
                {s.status === "pending" && (
                  <button
                    type="button"
                    onClick={() => handleCancelScheduled(s.id)}
                    data-testid={`scheduled-cancel-${s.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#e5e3db] px-3 py-1.5 text-xs text-[#5c5c5c] hover:border-[#d9381e] hover:text-[#d9381e] transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
                    Cancelar
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Confirmation modal */}
      {confirming && (
        <div
          data-testid="broadcast-confirm-modal"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[#1e2a5a]/60 backdrop-blur-sm p-6"
          onClick={() => setConfirming(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-display text-2xl text-[#1e2a5a]">
              {mode === "schedule" ? "Confirma o agendamento?" : "Confirma o envio?"}
            </p>
            <p className="mt-3 text-sm text-[#5c5c5c] leading-relaxed">
              {mode === "schedule" ? (
                <>
                  Vamos agendar{" "}
                  <strong className="text-[#1e2a5a]">{segmentCount}</strong>{" "}
                  e-mail(s) para o segmento{" "}
                  <strong className="text-[#1e2a5a]">
                    {SEGMENT_OPTIONS.find((s) => s.value === segment)?.label ||
                      "Todos os inscritos"}
                  </strong>{" "}
                  no dia{" "}
                  <strong className="text-[#1e2a5a]">
                    {sendAt ? formatLocalDatetime(sendAt) : "—"}
                  </strong>
                  . Você pode cancelar até o momento do envio.
                </>
              ) : (
                <>
                  Você vai disparar{" "}
                  <strong className="text-[#1e2a5a]">{segmentCount}</strong>{" "}
                  e-mail(s) para o segmento{" "}
                  <strong className="text-[#1e2a5a]">
                    {SEGMENT_OPTIONS.find((s) => s.value === segment)?.label ||
                      "Todos os inscritos"}
                  </strong>
                  . Essa ação não pode ser desfeita.
                </>
              )}
            </p>
            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                data-testid="broadcast-cancel"
                className="rounded-full border border-[#e5e3db] px-5 py-2 text-sm text-[#5c5c5c] hover:border-[#1e2a5a] hover:text-[#1e2a5a] transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSend}
                disabled={sending}
                data-testid="broadcast-confirm-send"
                className="inline-flex items-center gap-2 rounded-full bg-[#d9381e] px-5 py-2 text-sm font-semibold text-[#f4f1ea] hover:bg-[#1e2a5a] transition-colors disabled:opacity-60"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {mode === "schedule" ? "Agendar" : "Enviar agora"}
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterBroadcast;
